import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text, Button, Group, Stack, TextInput, Badge, ActionIcon, Loader, Center } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconEdit, IconCheck, IconX } from '@tabler/icons-react'
import { getStockLocations, createStockLocation, updateStockLocation } from '../../api/client'

export default function StockLocationsTab() {
  const { t } = useTranslation()
  const [locations, setLocations] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [newCode, setNewCode] = useState('')
  const [newName, setNewName] = useState('')
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  const load = async () => {
    setLoading(true)
    try { const { data } = await getStockLocations(); setLocations(data) }
    catch {} finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleAdd = async () => {
    if (!newCode.trim() || !newName.trim()) {
      notifications.show({ color: 'red', message: t('common.required') }); return
    }
    setAdding(true)
    try {
      await createStockLocation({ code: newCode.trim().toUpperCase(), name: newName.trim() })
      notifications.show({ color: 'teal', message: t('common.success') })
      setNewCode(''); setNewName('')
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setAdding(false) }
  }

  const handleEdit = async (id: number) => {
    if (!editName.trim()) return
    try {
      await updateStockLocation(id, { name: editName.trim() })
      notifications.show({ color: 'teal', message: t('common.success') })
      setEditingId(null); load()
    } catch { notifications.show({ color: 'red', message: t('common.error') }) }
  }

  const handleToggleActive = async (loc: any) => {
    try { await updateStockLocation(loc.id, { isActive: !loc.isActive }); load() }
    catch { notifications.show({ color: 'red', message: t('common.error') }) }
  }

  return (
    <Stack gap="md">
      <Box>
        <Text fw={600} mb={2}>{t('config.stockLocations','คลังสต็อก')}</Text>
        <Text size="xs" c="dimmed">สถานที่จัดเก็บของจริง เช่น "คลังกลาง", "คลังแม่บ้านชั้น 3" — แยกจาก Location ของ Asset</Text>
      </Box>

      <Box style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0' }}>
        <Text size="sm" fw={600} mb={10}>เพิ่มคลังใหม่</Text>
        <Group gap="sm" align="flex-end">
          <TextInput label="Code" placeholder="เช่น HK-F3" value={newCode}
            onChange={e => setNewCode(e.target.value)} style={{ width: 130 }} />
          <TextInput label="ชื่อคลัง" placeholder="เช่น คลังแม่บ้านชั้น 3" value={newName}
            onChange={e => setNewName(e.target.value)} style={{ flex: 1 }} />
          <Button loading={adding} onClick={handleAdd} leftSection={<IconPlus size={14} />}
            style={{ background: 'var(--brand-primary,#0f172a)', color: 'var(--brand-on-primary,#fff)', border: '1px solid var(--brand-border,transparent)' }}>
            เพิ่ม
          </Button>
        </Group>
      </Box>

      {loading ? <Center py="xl"><Loader size="sm" /></Center> : (
        <Stack gap={6}>
          {locations.map(loc => (
            <Box key={loc.id} style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
              <Group justify="space-between">
                {editingId === loc.id ? (
                  <Group gap="sm" style={{ flex: 1 }}>
                    <TextInput size="xs" value={editName}
                      onChange={e => setEditName(e.target.value)} style={{ flex: 1 }} />
                    <ActionIcon size="sm" color="teal" onClick={() => handleEdit(loc.id)}>
                      <IconCheck size={14} />
                    </ActionIcon>
                    <ActionIcon size="sm" variant="subtle" onClick={() => setEditingId(null)}>
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                ) : (
                  <>
                    <Group gap={8}>
                      <Text size="sm" fw={600}>{loc.name}</Text>
                      <Badge size="xs" variant="outline" color="gray">{loc.code}</Badge>
                    </Group>
                    <Group gap={6}>
                      <Badge size="xs" color={loc.isActive ? 'teal' : 'gray'} variant="light"
                        style={{ cursor: 'pointer' }} onClick={() => handleToggleActive(loc)}>
                        {loc.isActive ? 'ใช้งาน' : 'ปิดใช้'}
                      </Badge>
                      <ActionIcon size="sm" variant="subtle"
                        onClick={() => { setEditingId(loc.id); setEditName(loc.name) }}>
                        <IconEdit size={14} />
                      </ActionIcon>
                    </Group>
                  </>
                )}
              </Group>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
